import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  Upload,
  Image,
  File,
  X,
  FolderOpen,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import { useFileQueue } from '../contexts/AppContext';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '@clerk/clerk-react';
import {
  validateFileSize,
  sortFilesInvalidFirst,
  partitionFiles,
  FileValidation
} from '../utils/fileValidation';

// Extend input element to support non-standard directory selection attributes
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface InputHTMLAttributes<T> {
    webkitdirectory?: string;
    directory?: string;
  }
}

interface FilePreview {
  file: File;
  id: string;
  preview?: string;
  isImage: boolean;
  size: string;
  queueId?: string; // ID from the global fileQueue
  validation: FileValidation;
}

const LocalFilePicker: React.FC = () => {
  const [dragOver, setDragOver] = useState(false);
  const [previews, setPreviews] = useState<FilePreview[]>([]);
  const [showSelectionOptions, setShowSelectionOptions] = useState(false);
  const [validCount, setValidCount] = useState(0);
  const [invalidCount, setInvalidCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const { addFiles, removeFile, clearQueue, state } = useFileQueue();
  const { success, warning, error } = useToast();
  const navigate = useNavigate();
  const { isSignedIn } = useUser();

  // Supported image formats
  const supportedFormats = React.useMemo(() => 
    ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/heic', 'image/heif'], 
    []
  );

  // Check for directory selection support (cross-platform)
  const supportsDirectorySelection = React.useMemo(() => {
    try {
      const input = document.createElement('input');
      return 'webkitdirectory' in input;
    } catch {
      return false;
    }
  }, []);

  const formatFileSize = (bytes: number): string => {
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const generateFileId = (): string => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  const createFilePreview = useCallback((file: File): FilePreview => {
    const isImage = supportedFormats.includes(file.type);
    const isHeicFile = file.type === 'image/heic' || file.type === 'image/heif' ||
                       file.name.toLowerCase().endsWith('.heic') ||
                       file.name.toLowerCase().endsWith('.heif');
    let preview: string | undefined;

    // Skip preview generation for HEIC/HEIF files since browsers can't display them natively
    // The backend will handle HEIC conversion, but frontend preview would fail
    if (isImage && !isHeicFile) {
      try {
        preview = URL.createObjectURL(file);
      } catch (error) {
        console.warn('Failed to generate preview for:', file.name, error);
      }
    }

    // Validate file size
    const validation = validateFileSize(file);

    return {
      file,
      id: generateFileId(),
      preview,
      isImage,
      size: formatFileSize(file.size),
      validation
    };
  }, [supportedFormats]);

  const processFiles = useCallback(async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    const imageFiles = files.filter(file => supportedFormats.includes(file.type));
    
    if (imageFiles.length === 0) {
      warning('No image files selected', 'Please select image files. Supported formats: JPEG, PNG, GIF, BMP, WEBP');
      return;
    }

    // Check absolute maximum file limit (100,000 files for all users)
    if (imageFiles.length > 100000) {
      error(
        'Too many files selected',
        `SortingCat web max job size is 100,000 files! You selected ${imageFiles.length.toLocaleString()} images. Contact support@sortingcat.ai to discuss our bulk enterprise rates and API access.`
      );
      return;
    }

    // Check file count limit (10 images for free tier - only for unauthenticated users)
    if (!isSignedIn && imageFiles.length > 10) {
      // Create previews for first 3 images before showing error
      try {
        const previewFiles = imageFiles.slice(0, 3);
        const limitPreviews = previewFiles.map((file) => {
          const preview = createFilePreview(file);
          return {
            name: preview.file.name,
            size: preview.size,
            preview: preview.preview,
            isImage: preview.isImage,
            actualSize: preview.file.size
          };
        });

        // Persist preview data to sessionStorage for plans page
        const previewData = limitPreviews.map(preview => ({
          name: preview.name,
          size: preview.size,
          preview: preview.preview,
          isImage: preview.isImage
        }));

        sessionStorage.setItem('sortingcat_limit_previews', JSON.stringify(previewData));
        sessionStorage.setItem('sortingcat_limit_reason', JSON.stringify({
          type: 'count',
          attempted: imageFiles.length,
          limit: 10,
          message: `You selected ${imageFiles.length} images. The free service supports fewer images per job.`
        }));
      } catch (err) {
        console.warn('Failed to create limit previews:', err);
      }

      error(
        'Too many files selected',
        `You selected ${imageFiles.length} images. The free service supports fewer images per job. Please select fewer files or upgrade to our paid service for larger batches.`
      );
      // Redirect to plans page using TanStack Router with actual image count
      setTimeout(() => {
        navigate({ to: '/plans', search: { from: 'limit', imageCount: imageFiles.length, selected_plan: undefined } });
      }, 3000);
      return;
    }

    // Allow all files to be selected - payment flow handled at submission time

    if (imageFiles.length !== files.length) {
      const skipped = files.length - imageFiles.length;
      warning('Some files skipped', `${skipped} non-image file(s) were skipped. Only image files are supported.`);
    }

    try {
      const newPreviews = imageFiles.map(createFilePreview);
      setPreviews(prev => [...prev, ...newPreviews]);

      // Auto-queue files immediately after processing
      // The queueIds will be synced in the useEffect
      addFiles(imageFiles);

      // Show success feedback
      const successMessage = `Added ${imageFiles.length} image${imageFiles.length !== 1 ? 's' : ''} to queue!`;
      success('Files queued successfully', successMessage);
      console.log(`✅ Auto-queued: ${successMessage}`);

    } catch (err) {
      console.error('Error processing files:', err);
      error('Processing failed', 'Error processing some files. Please try again.');
    }
  }, [supportedFormats, createFilePreview, addFiles, success, warning, error, navigate, isSignedIn]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const {files} = event.target;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset the input so the same files can be selected again if needed
    event.target.value = '';
  }, [processFiles]);

  const handleFolderSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const {files} = event.target;
    if (files && files.length > 0) {
      processFiles(files);
    }
    event.target.value = '';
  }, [processFiles]);

  // Unified selection handler for cross-platform UX
  const handleUnifiedSelection = useCallback(() => {
    if (!supportsDirectorySelection) {
      // Fall back to files only on browsers without directory support
      fileInputRef.current?.click();
    } else {
      // Show selection options for modern browsers
      setShowSelectionOptions(!showSelectionOptions);
    }
  }, [supportsDirectorySelection, showSelectionOptions]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    const {files} = event.dataTransfer;
    if (files && files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  const removePreview = useCallback((id: string) => {
    // Find the preview to get its queueId
    const preview = previews.find(p => p.id === id);

    // Remove from local previews
    setPreviews(prev => {
      // No cleanup needed for data URLs (they're self-contained)
      return prev.filter(p => p.id !== id);
    });

    // Also remove from global queue if we have the queueId
    if (preview?.queueId) {
      removeFile(preview.queueId);
    }
  }, [previews, removeFile]);

  // Removed clearAll function - using clearPreviews instead

  const clearPreviews = useCallback(() => {
    setPreviews(() => {
      // No cleanup needed for data URLs (they're self-contained)
      return [];
    });
    // Also clear the global file queue
    clearQueue();
  }, [clearQueue]);

  // Update validation counts when previews change
  useEffect(() => {
    const { valid, invalid } = partitionFiles(previews);
    setValidCount(valid.length);
    setInvalidCount(invalid.length);
  }, [previews]);

  // Sort previews with invalid files first
  const sortedPreviews = useMemo(() =>
    sortFilesInvalidFirst(previews),
    [previews]
  );

  // Sync local previews with global queue state - fixes accumulation issue
  React.useEffect(() => {
    // Clear previews when global queue is cleared (after successful upload)
    if (state.fileQueue.length === 0 && previews.length > 0) {
      console.log('🧹 Auto-clearing previews to sync with empty queue');
      clearPreviews();
      return;
    }

    // Sync preview queueIds with the fileQueue
    // Match files by File object reference
    setPreviews(prev => {
      return prev.map(preview => {
        // Find matching queue item by File object reference
        const queueItem = state.fileQueue.find(q => q.file === preview.file);
        if (queueItem && preview.queueId !== queueItem.id) {
          return { ...preview, queueId: queueItem.id };
        }
        return preview;
      });
    });
  }, [state.fileQueue, previews.length, clearPreviews]);

  // No cleanup needed for data URLs when component unmounts
  // Data URLs are garbage collected automatically with the component

  return (
    <div className="w-full">
      <div className="p-4 border-b border-gray-200 text-center">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-center">
          <FolderOpen className="w-5 h-5 mr-2" />
          1) Select Files
        </h2>
      </div>

      <div className="p-4">
        {/* File Selection Area */}
        <div
          className={clsx(
            'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop images here, or press to select
          </h3>
          {/* <p className="text-sm text-gray-600 mb-4">
            Supports: JPEG, PNG, GIF, BMP, WEBP, HEIC, HEIF
          </p> */}
          
          {/* Mobile-First Consolidated Selection with Proper Touch Targets */}
          <div className="flex flex-col items-center justify-center space-y-4 px-4">
            {/* Primary CTA Button - Mobile Optimized with Animation */}
            <motion.button
              onClick={handleUnifiedSelection}
              className={clsx(
                "inline-flex items-center justify-center",
                "min-h-[56px] sm:min-h-[66px] px-6 sm:px-9 py-4 sm:py-[18px]",
                "text-white font-semibold text-base sm:text-xl md:text-2xl",
                "rounded-xl shadow-lg",
                "focus:outline-none focus:ring-2 focus:ring-offset-2",
                "touch-manipulation select-none",
                "w-full max-w-xs sm:max-w-sm sm:w-auto",
                (showSelectionOptions || previews.length > 0)
                  ? "bg-green-600 hover:bg-green-700 active:bg-green-800 focus:ring-green-500"
                  : "bg-purple-600 hover:bg-purple-700 active:bg-purple-800 focus:ring-purple-500"
              )}
              type="button"
              aria-expanded={showSelectionOptions}
              aria-label={!supportsDirectorySelection ? 'Select image files' : 'Choose image selection method'}
              initial={{ scale: 1 }}
              animate={(showSelectionOptions || previews.length > 0) ? { scale: 1 } : {
                scale: [1, 1.042, 1],
              }}
              transition={(showSelectionOptions || previews.length > 0) ? { duration: 0.2 } : {
                duration: 1.5,
                ease: "easeInOut",
                repeat: Infinity,
                repeatDelay: 0.5
              }}
              whileHover={(showSelectionOptions || previews.length > 0) ? {} : {
                scale: 1.07,
                transition: { duration: 0.2 }
              }}
              whileTap={{
                scale: 0.95,
                transition: { duration: 0.1 }
              }}
            >
              <FolderOpen className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 mr-2 sm:mr-3 flex-shrink-0" />
              {(showSelectionOptions || previews.length > 0) ? (
                <span className="whitespace-nowrap">
                  {!supportsDirectorySelection ? 'Select Images' : 'Upload Images'}
                </span>
              ) : (
                <span className="whitespace-nowrap flex">
                  {(!supportsDirectorySelection ? 'Select Images' : 'Upload Images').split('').map((letter, index) => (
                    <motion.span
                      key={index}
                      animate={{
                        color: ['#ffffff', '#ffb347', '#ffffff'],
                      }}
                      transition={{
                        duration: 1.5,
                        ease: "easeInOut",
                        repeat: Infinity,
                        delay: index * 0.1,
                      }}
                    >
                      {letter === ' ' ? '\u00A0' : letter}
                    </motion.span>
                  ))}
                </span>
              )}
            </motion.button>

            {/* Progressive Disclosure - Responsive Layout */}
            {supportsDirectorySelection && showSelectionOptions && (
              <React.Fragment>
                <motion.div
                  className="
                    flex flex-col sm:flex-row items-center justify-center
                    space-y-3 sm:space-y-0 sm:space-x-4
                    w-full max-w-md
                  "
                  role="group"
                  aria-label="File selection options"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.button
                    onClick={() => {
                      fileInputRef.current?.click();
                      setShowSelectionOptions(false);
                    }}
                    className="
                      inline-flex items-center justify-center
                      min-h-[48px] sm:min-h-[55px] px-5 sm:px-8 py-3 sm:py-4
                      bg-purple-600 hover:bg-purple-700 active:bg-purple-800
                      text-white font-semibold text-sm sm:text-base md:text-lg
                      rounded-xl shadow-md
                      focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                      touch-manipulation select-none
                      w-full sm:w-auto sm:min-w-[130px] md:min-w-[150px]
                    "
                    type="button"
                    aria-label="Select individual image files"
                    animate={{ scale: [1, 1.042, 1] }}
                    transition={{
                      duration: 1.5,
                      ease: "easeInOut",
                      repeat: Infinity,
                      repeatDelay: 0.5
                    }}
                    whileHover={{ scale: 1.07, transition: { duration: 0.2 } }}
                    whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                  >
                    <File className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span className="flex">
                      {/* Files text - wave starts after Folder completes (delay offset: 0.8s) */}
                      {'Files'.split('').map((letter, index) => (
                        <motion.span
                          key={index}
                          animate={{ color: ['#ffffff', '#ffb347', '#ffffff'] }}
                          transition={{
                            duration: 0.4,
                            ease: "easeInOut",
                            repeat: Infinity,
                            repeatDelay: 1.1,
                            delay: 0.8 + (index * 0.1),
                          }}
                        >
                          {letter}
                        </motion.span>
                      ))}
                    </span>
                  </motion.button>

                  <div className="text-base sm:text-lg md:text-xl text-black font-bold px-2 sm:px-3" aria-hidden="true">
                    or
                  </div>

                  <motion.button
                    onClick={() => {
                      folderInputRef.current?.click();
                      setShowSelectionOptions(false);
                    }}
                    className="
                      inline-flex items-center justify-center
                      min-h-[48px] sm:min-h-[55px] px-5 sm:px-8 py-3 sm:py-4
                      bg-purple-600 hover:bg-purple-700 active:bg-purple-800
                      text-white font-semibold text-sm sm:text-base md:text-lg
                      rounded-xl shadow-md
                      focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                      touch-manipulation select-none
                      w-full sm:w-auto sm:min-w-[130px] md:min-w-[150px]
                    "
                    type="button"
                    aria-label="Select entire folder of images"
                    animate={{ scale: [1, 1.042, 1] }}
                    transition={{
                      duration: 1.5,
                      ease: "easeInOut",
                      repeat: Infinity,
                      repeatDelay: 0.5
                    }}
                    whileHover={{ scale: 1.07, transition: { duration: 0.2 } }}
                    whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                  >
                    <FolderOpen className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span className="flex">
                      {/* Folder text - wave starts first (no delay offset) */}
                      {'Folder'.split('').map((letter, index) => (
                        <motion.span
                          key={index}
                          animate={{ color: ['#ffffff', '#ffb347', '#ffffff'] }}
                          transition={{
                            duration: 0.4,
                            ease: "easeInOut",
                            repeat: Infinity,
                            repeatDelay: 1.1,
                            delay: index * 0.1,
                          }}
                        >
                          {letter}
                        </motion.span>
                      ))}
                    </span>
                  </motion.button>
                </motion.div>
              </React.Fragment>
            )}
            
            {/* Contextual Help Text - Responsive Typography */}
            {!supportsDirectorySelection && (
              <div className="text-center max-w-sm">
                <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                  Individual file selection only
                  <span className="block sm:inline">
                    <span className="hidden sm:inline"> - </span>
                    folder selection not supported
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={folderInputRef}
            type="file"
            accept="image/*"
            multiple
            webkitdirectory=""
            onChange={handleFolderSelect}
            className="hidden"
          />
        </div>

        {/* File Previews */}
        {previews.length > 0 && (
          <div className="mt-6">
            {/* Warning Banner for Invalid Files */}
            {invalidCount > 0 && (
              <div
                className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg"
                role="alert"
                aria-live="polite"
              >
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-orange-900 mb-1">
                     Meow! {invalidCount} file{invalidCount !== 1 ? 's are' : ' is'} too chunky for my sorting paws 🐾 (over 10 MB)
                    </h4>
                    <p className="text-sm text-orange-800">
                      {invalidCount} file{invalidCount !== 1 ? 's' : ''}  will be excluded.
                      You can remove {invalidCount === 1 ? 'it' : 'them'} and select smaller files.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Screen reader announcement region */}
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              {validCount} valid file{validCount !== 1 ? 's' : ''} selected
              {invalidCount > 0 && `, ${invalidCount} file${invalidCount !== 1 ? 's' : ''} exceed size limit`}
            </div>

            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900">
                Files Added to Queue ({validCount} valid
                {invalidCount > 0 && `, ${invalidCount} invalid`})
              </h4>
              <div className="flex items-center space-x-2">
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Auto-queued
                </div>
                <button
                  onClick={clearPreviews}
                  className="
                    text-sm text-gray-500 hover:text-gray-700
                    focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2
                    rounded-md px-2 py-1 transition-colors duration-200
                  "
                  type="button"
                  aria-label="Clear all previews"
                >
                  Clear All
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 max-h-64 overflow-y-auto">
              {sortedPreviews.map((preview) => (
                <div key={preview.id} className="relative group">
                  <div className={clsx(
                    "aspect-square bg-gray-100 rounded-lg overflow-hidden",
                    preview.validation.isValid
                      ? "border border-gray-200"
                      : "border-2 border-red-500 ring-2 ring-red-200"
                  )}>
                    {preview.preview ? (
                      <img
                        src={preview.preview}
                        alt={preview.file.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.warn('Preview load failed for:', preview.file.name);
                          // Hide the broken image
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
                        <Image className="w-8 h-8 text-gray-400 mb-2" />
                        {(preview.file.type === 'image/heic' || preview.file.type === 'image/heif' ||
                          preview.file.name.toLowerCase().endsWith('.heic') ||
                          preview.file.name.toLowerCase().endsWith('.heif')) && (
                          <span className="text-xs text-gray-500 font-mono px-2 py-1 bg-blue-100 rounded">
                            HEIC
                          </span>
                        )}
                      </div>
                    )}

                    {/* Invalid file badge */}
                    {!preview.validation.isValid && (
                      <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center shadow-lg">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Too Large
                      </div>
                    )}

                    <button
                      onClick={() => removePreview(preview.id)}
                      className="
                        absolute top-2 right-2 w-6 h-6
                        bg-red-500 hover:bg-red-600 active:bg-red-700
                        text-white rounded-full
                        flex items-center justify-center
                        opacity-0 group-hover:opacity-100 focus:opacity-100
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1
                      "
                      type="button"
                      title="Remove file"
                      aria-label={`Remove ${preview.file.name} from selection`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="mt-2 text-xs text-gray-600 truncate">
                    <p className="font-medium truncate">{preview.file.name}</p>
                    <p className={clsx(
                      preview.validation.isValid ? "text-gray-500" : "text-red-600 font-semibold"
                    )}>
                      {preview.size}
                      {!preview.validation.isValid && " (> 10 MB)"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default LocalFilePicker;